/**
 * Notification System Service
 * Problem 9: Benachrichtigungssystem für Neuigkeiten
 * Handles user notifications, team broadcasts, @mentions, and system events
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

export interface Notification {
  id: string;
  company_id: string;
  user_id: string | null;
  type: string;
  title: string;
  message: string;
  related_to_type?: string | null;
  related_to_id?: string | null;
  triggered_by_user_id?: string | null;
  triggered_by_name?: string | null;
  is_read: boolean;
  read_at?: string | null;
  metadata?: any;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  created_at: string;
  updated_at: string;
}

export interface NotificationStatistics {
  unread_count: number;
  total_count: number;
  last_24h_count: number;
  unread_last_week_count: number;
  latest_notification_at?: string | null;
}

export interface NotificationPreferences {
  id: string;
  user_id: string;
  company_id: string;
  enabled_types: string[];
  in_app_enabled: boolean;
  email_enabled: boolean;
  push_enabled: boolean;
  quiet_hours_start?: string | null;
  quiet_hours_end?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateNotificationRequest {
  userId?: string;
  type: string;
  title: string;
  message: string;
  relatedToType?: string;
  relatedToId?: string;
  metadata?: any;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
}

export interface BroadcastNotificationRequest {
  type: string;
  title: string;
  message: string;
  relatedToType?: string;
  relatedToId?: string;
  metadata?: any;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  excludeUserId?: string;
}

class NotificationSystemService {
  /**
   * Get user's notifications
   */
  async getNotifications(options?: {
    limit?: number;
    offset?: number;
    unreadOnly?: boolean;
    type?: string;
  }): Promise<{
    notifications: Notification[];
    total: number;
  }> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    if (options?.unreadOnly) params.append('unreadOnly', 'true');
    if (options?.type) params.append('type', options.type);

    const response = await fetch(
      `${API_BASE_URL}/notifications?${params.toString()}`,
      {
        method: 'GET',
        credentials: 'include',
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch notifications');
    }

    const result = await response.json();
    return {
      notifications: result.notifications,
      total: result.total,
    };
  }

  /**
   * Get unread notifications count
   */
  async getUnreadCount(): Promise<number> {
    const response = await fetch(`${API_BASE_URL}/notifications/unread-count`, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch unread count');
    }

    const result = await response.json();
    return result.count;
  }

  /**
   * Get notification statistics
   */
  async getStatistics(): Promise<NotificationStatistics> {
    const response = await fetch(`${API_BASE_URL}/notifications/statistics`, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch statistics');
    }

    const result = await response.json();
    return result.statistics;
  }

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId: string): Promise<Notification> {
    const response = await fetch(
      `${API_BASE_URL}/notifications/${notificationId}/read`,
      {
        method: 'PATCH',
        credentials: 'include',
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to mark notification as read');
    }

    const result = await response.json();
    return result.notification;
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<{ count: number }> {
    const response = await fetch(
      `${API_BASE_URL}/notifications/mark-all-read`,
      {
        method: 'POST',
        credentials: 'include',
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to mark all as read');
    }

    const result = await response.json();
    return { count: result.count };
  }

  /**
   * Delete notification
   */
  async deleteNotification(notificationId: string): Promise<void> {
    const response = await fetch(
      `${API_BASE_URL}/notifications/${notificationId}`,
      {
        method: 'DELETE',
        credentials: 'include',
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete notification');
    }
  }

  /**
   * Get notification preferences
   */
  async getPreferences(): Promise<NotificationPreferences> {
    const response = await fetch(`${API_BASE_URL}/notifications/preferences`, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch preferences');
    }

    const result = await response.json();
    return result.preferences;
  }

  /**
   * Update notification preferences
   */
  async updatePreferences(
    preferences: Partial<NotificationPreferences>
  ): Promise<NotificationPreferences> {
    const response = await fetch(`${API_BASE_URL}/notifications/preferences`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(preferences),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update preferences');
    }

    const result = await response.json();
    return result.preferences;
  }

  /**
   * Create notification
   */
  async createNotification(
    data: CreateNotificationRequest
  ): Promise<Notification> {
    const response = await fetch(`${API_BASE_URL}/notifications/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create notification');
    }

    const result = await response.json();
    return result.notification;
  }

  /**
   * Broadcast notification to team
   */
  async broadcastToTeam(
    data: BroadcastNotificationRequest
  ): Promise<{ notifications: Notification[]; count: number }> {
    const response = await fetch(`${API_BASE_URL}/notifications/broadcast`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to broadcast notification');
    }

    const result = await response.json();
    return {
      notifications: result.notifications,
      count: result.count,
    };
  }

  /**
   * Format notification type for display (German)
   */
  formatNotificationType(type: string): string {
    const typeMap: Record<string, string> = {
      candidate_added: 'Neuer Kandidat',
      company_added: 'Neue Firma',
      contact_added: 'Neuer Kontakt',
      mention: 'Erwähnung',
      assignment: 'Zuweisung',
      email_received: 'Neue E-Mail',
      task_assigned: 'Aufgabe zugewiesen',
      edit_made: 'Bearbeitung',
      event_created: 'Termin erstellt',
      status_changed: 'Status geändert',
    };

    return typeMap[type] || type;
  }

  /**
   * Get icon for notification type
   */
  getNotificationIcon(type: string): string {
    const iconMap: Record<string, string> = {
      candidate_added: '👤',
      company_added: '🏢',
      contact_added: '📇',
      mention: '@',
      assignment: '📌',
      email_received: '📧',
      task_assigned: '✓',
      edit_made: '✏️',
      event_created: '📅',
      status_changed: '🔄',
    };

    return iconMap[type] || '🔔';
  }

  /**
   * Get color for notification priority
   */
  getPriorityColor(priority: string): string {
    const colorMap: Record<string, string> = {
      low: '#6B7280',
      normal: '#3B82F6',
      high: '#F59E0B',
      urgent: '#EF4444',
    };

    return colorMap[priority] || colorMap.normal;
  }

  /**
   * Get background color for notification type
   */
  getTypeBackgroundColor(type: string): string {
    const colorMap: Record<string, string> = {
      candidate_added: '#EFF6FF',
      company_added: '#F0FDF4',
      contact_added: '#FEF3C7',
      mention: '#FEF2F2',
      assignment: '#F3E8FF',
      email_received: '#EFF6FF',
      task_assigned: '#DBEAFE',
      edit_made: '#F3F4F6',
      event_created: '#FCE7F3',
      status_changed: '#E0F2FE',
    };

    return colorMap[type] || '#F9FAFB';
  }

  /**
   * Format relative time (e.g., "vor 2 Stunden")
   */
  formatRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMinutes < 1) return 'gerade eben';
    if (diffMinutes < 60)
      return `vor ${diffMinutes} Minute${diffMinutes > 1 ? 'n' : ''}`;
    if (diffHours < 24)
      return `vor ${diffHours} Stunde${diffHours > 1 ? 'n' : ''}`;
    if (diffDays < 7) return `vor ${diffDays} Tag${diffDays > 1 ? 'en' : ''}`;
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `vor ${weeks} Woche${weeks > 1 ? 'n' : ''}`;
    }

    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  /**
   * Generate notification link based on related entity
   */
  getNotificationLink(notification: Notification): string | null {
    if (!notification.related_to_type || !notification.related_to_id) {
      return null;
    }

    const linkMap: Record<string, string> = {
      candidate: `/candidates/${notification.related_to_id}`,
      company: `/companies/${notification.related_to_id}`,
      contact: `/contacts/${notification.related_to_id}`,
      email: `/emails/${notification.related_to_id}`,
      task: `/tasks/${notification.related_to_id}`,
      event: `/calendar/${notification.related_to_id}`,
    };

    return linkMap[notification.related_to_type] || null;
  }

  /**
   * Check if user should be notified based on preferences
   */
  shouldNotify(
    notificationType: string,
    preferences: NotificationPreferences
  ): boolean {
    if (!preferences.in_app_enabled) {
      return false;
    }

    if (!preferences.enabled_types.includes(notificationType)) {
      return false;
    }

    // Check quiet hours (if implemented)
    // ... quiet hours logic

    return true;
  }
}

export const notificationSystemService = new NotificationSystemService();
export default notificationSystemService;
